import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';
import { FixedValue, FixedValues, MappingValue } from 'src/entities/import';
import { PlaceEntity } from 'src/entities/placeEntity';
import { ShipperEntity } from 'src/entities/shipperEntity';
import useImport from 'src/hooks/useImport';
import { useImportFixedValueMasters } from 'src/hooks/useImportFixedValueMasters';
import { useQueryPlaces } from 'src/hooks/useQueryPlaces';
import { useQueryShippers } from 'src/hooks/useQueryShippers';

import {
  addressMappingSelections,
  buildAddressMappingRows,
  canAddAddressMappingRow,
  MAX_ADDRESS_MAPPING_ROWS,
  nextAddressMappingRowCountAfterRemoval,
  removeAddressMappingSelection,
  setAddressMappingSelection,
} from './addressMappingRows';
import FactorNotice, { FactorChip, FactorHint, factorOfField, FactorPreview } from './FactorNotice';
import FixedValueInput from './FixedValueInput';
import {
  applyPlaceMaster,
  applyShipperMaster,
  FIXED_FIELD_TYPES,
  FixedFieldType,
  fixedModeKeysOf,
  fixedValuePreview,
  isFixedValueSet,
  TIME_GROUPS,
  timeGroupOf,
} from './fixedValues';
import Footer from './Footer';
import Header from './Header';
import { invalidSingleValueMappings, mappingValues, singleMappingValue } from './mappingValues';
import { ADDRESS_MAPPING_KEYS, selectableCsvColumnOptions } from './selectableMappingColumns';

type Side = 'loading' | 'unloading';
type Mode = 'column' | 'fixed';

const SHIPPER_KEYS = ['shipper_name', 'shipper_contact_person', 'shipper_phone_number', 'shipper_email_address'];

const placeKeysOf = (side: Side) => [`${side}_address`, `${side}_staying_minutes`, ...TIME_GROUPS[side]];

const SIDE_LABELS: Record<Side, string> = { loading: '積地', unloading: '降地' };

// 時間指定は API がその側の 6 項目をまとめて扱うので、1 項目の切り替えを 6 項目に広げる
const keysSwitchedWith = (key: string): string[] => {
  const side = timeGroupOf(key);
  return side ? TIME_GROUPS[side] : [key];
};

const withoutKeys = (values: FixedValues['values'], keys: string[]): FixedValues['values'] => (
  Object.fromEntries(Object.entries(values).filter(([key]) => !keys.includes(key)))
);

type BadRequestBody = { message?: string; errors?: Record<string, unknown> };

// 400 は送った対応付け・固定値の不備で、汎用エラーでは直す場所が分からないので API の文言と項目名を出す
const badRequestMessage = (error: unknown, labelOf: (key: string) => string): string | null => {
  if (!axios.isAxiosError<BadRequestBody>(error) || error.response?.status !== 400) return null;
  const { message, errors } = error.response.data ?? {};
  if (!message) return null;
  const labels = Object.keys(errors ?? {}).map(labelOf);
  return labels.length > 0 ? `${message}（${labels.join('、')}）` : message;
};

const Mapping: FC = memo(() => {
  const importContext = useImport();
  const licenseContext = useContext(LicenseContext);

  const customInputFields: string[] = licenseContext?.config?.custom_input_fields || [];
  const companyId: number = licenseContext?.config?.selected_company_id;

  const { data: shippers, isLoading: isLoadingShippers } = useQueryShippers(true);
  const { data: places, isLoading: isLoadingPlaces } = useQueryPlaces(true);
  const masters = useImportFixedValueMasters(companyId);

  const shipperOptions = useMemo(() => {
    const uniqueShippers = new Map<string, ShipperEntity>();
    (shippers ?? []).forEach((it) => {
      if (!uniqueShippers.has(it.name)) uniqueShippers.set(it.name, it);
    });
    return Array.from(uniqueShippers.values());
  }, [shippers]);

  // 地点一覧は全社表示などで他社の地点も返すので、取込む会社の地点だけを候補にする。
  // 全社表示（selected_company_id 0）の取込はログイン会社に入るので、ログイン会社で絞る
  const importCompanyId: number = companyId || licenseContext?.config?.company_id;
  const placeOptions = useMemo(() => {
    const ownPlaces = (places ?? []).filter((it) => it.company_id === importCompanyId);
    return {
      loading: ownPlaces.filter((it) => it.location_role !== 'unloading'),
      unloading: ownPlaces.filter((it) => it.location_role !== 'loading'),
    };
  }, [places, importCompanyId]);

  const previewLabels = useMemo(() => ({
    trucks: new Map(masters.trucks.map((it) => [it.id, it.label])),
    drivers: new Map(masters.drivers.map((it) => [it.id, it.label])),
  }), [masters.trucks, masters.drivers]);

  const { enqueueSnackbar } = useSnackbar();
  const isAddressMapping = (key: string) => ADDRESS_MAPPING_KEYS.has(key);
  const selectableColumns = (obj: { en: string; onlyData?: boolean }, currentValue: string | null = null) => {
    const source = obj.onlyData ? importContext.only_datetime_csv_columns : importContext.csvColumns;

    return selectableCsvColumnOptions(source, importContext.mappings, obj.en, currentValue);
  };

  const attributesForImport: { en: string, ja: string, required: boolean, onlyData?: boolean }[] = [
    ...[
      { en: 'code', ja: '伝票No', required: false },
      { en: 'delivery_method', ja: '配送形態', required: false },
      { en: 'section_type', ja: '区間種別', required: false },
      { en: 'shipper_name', ja: '荷主名', required: false },
      { en: 'shipper_contact_person', ja: '荷主担当者', required: false },
      { en: 'shipper_phone_number', ja: '荷主電話番号', required: false },
      { en: 'shipper_email_address', ja: '荷主メールアドレス', required: false },
      { en: 'loading_name', ja: '積地名称', required: true },
      { en: 'loading_address', ja: '積地住所', required: false },
      { en: 'loading_on', ja: '積日', required: true, onlyData: true },
      { en: 'loading_at_start', ja: '積地時間指定（から)', required: false },
      { en: 'loading_at_end', ja: '積地時間指定（まで）', required: false },
      { en: 'loading_at_start_two', ja: '積地時間指定②（から)', required: false },
      { en: 'loading_at_end_two', ja: '積地時間指定②（まで）', required: false },
      { en: 'loading_at_start_three', ja: '積地時間指定③（から)', required: false },
      { en: 'loading_at_end_three', ja: '積地時間指定③（まで）', required: false },
      { en: 'loading_staying_minutes', ja: '積地作業分数', required: false },
      { en: 'unloading_name', ja: '降地名称', required: true },
      { en: 'unloading_address', ja: '降地住所', required: false },
      { en: 'unloading_on', ja: '降日', required: true, onlyData: true },
      { en: 'unloading_at_start', ja: '降地時間指定（から）', required: false },
      { en: 'unloading_at_end', ja: '降地時間指定（まで）', required: false },
      { en: 'unloading_at_start_two', ja: '降地時間指定②（から）', required: false },
      { en: 'unloading_at_end_two', ja: '降地時間指定②（まで）', required: false },
      { en: 'unloading_at_start_three', ja: '降地時間指定③（から）', required: false },
      { en: 'unloading_at_end_three', ja: '降地時間指定③（まで）', required: false },
      { en: 'unloading_staying_minutes', ja: '降地作業分数', required: false },
      { en: 'item_count', ja: '数量', required: true },
      { en: 'item_packing_style', ja: '荷姿', required: false },
      { en: 'item_total_weight_kg', ja: '総重量(kg/才)', required: true },
      { en: 'item_total_volume_m3', ja: '総体積(立米)', required: false },
      { en: 'item_code', ja: '商品コード', required: false },
      { en: 'item_name', ja: '品名', required: false },
      { en: 'item_lot_number', ja: 'ロットNo', required: false },
      { en: 'item_custom_order_number', ja: '特注No', required: false },
      { en: 'item_klass', ja: '輸送区分', required: false },
      { en: 'item_handling_of_cargo_style', ja: '荷扱い', required: false },
      { en: 'item_can_be_mixed', ja: '混載可', required: false },
      { en: 'designated_truck_klasses', ja: '車両タイプ', required: false },
      { en: 'designated_truck_car_models', ja: '車種', required: false },
      { en: 'designated_truck_loading_platform_heights', ja: '荷台高', required: false },
      { en: 'designated_truck_loading_platform_widths', ja: '荷台幅', required: false },
      { en: 'designated_truck_loading_platform_lengths', ja: '荷台長', required: false },
      { en: 'designated_truck_floor_specifications', ja: '床仕様', required: false },
      { en: 'designated_truck_features', ja: '装置', required: false },
      { en: 'billing_basic_fee_yen', ja: '請求 基本運賃', required: false },
      { en: 'billing_surcharge_fee_yen', ja: '請求 サーチャージ料', required: false },
      { en: 'billing_relay_fee_yen', ja: '請求 中継料', required: false },
      { en: 'billing_highway_fee_yen', ja: '請求 高速代金', required: false },
      { en: 'billing_loading_fee_yen', ja: '請求 積込料金', required: false },
      { en: 'billing_ancillary_fee_yen', ja: '請求 付帯作業料金', required: false },
      { en: 'billing_waiting_time_fee_yen', ja: '請求 待機料金', required: false },
      { en: 'billing_unloading_fee_yen', ja: '請求 取卸料', required: false },
      { en: 'billing_expenses_fee_yen', ja: '請求 諸経費', required: false },
      { en: 'billing_ancillary_content', ja: '請求 付帯作業備考', required: false },
      { en: 'memo', ja: '備考', required: false },
      { en: 'allowed_trucks', ja: '指定トラック', required: false },
      { en: 'denied_drivers', ja: 'NGドライバー', required: false },
    ],
    ...customInputFields.map((it) => ({
      en: it,
      ja: it,
      required: false
    }))
  ];

  const requiredColumns: { en: string; ja: string; required: boolean }[] = attributesForImport.filter((attributes) => attributes.required);

  const fixedTypeOf = (key: string): FixedFieldType | undefined => (
    FIXED_FIELD_TYPES[key] ?? (customInputFields.includes(key) ? 'text' : undefined)
  );

  const updateMappings = (key: string, value: MappingValue) => {
    importContext.setMappings({
      ...importContext.mappings,
      [key]: value
    });
  };

  const clearMappings = (keys: string[]) => {
    if (keys.every((key) => mappingValues(importContext.mappings[key]).length === 0)) return;

    importContext.setMappings({
      ...importContext.mappings,
      ...Object.fromEntries(keys.map((key) => [key, null])),
    });
  };

  // Why not importContext.setFixedValues に値を直接渡す: 同じイベントで 2 回更新することがある
  // （地点の自由入力は名称 → 地点 id の順）。Context の setter は値しか受けないので、
  // 描画前の importContext.fixedValues に重ねると 2 回目が 1 回目の名称を消す。最後に送った値に重ねる。
  const latestFixedValues = useRef(importContext.fixedValues);
  useLayoutEffect(() => {
    latestFixedValues.current = importContext.fixedValues;
  }, [importContext.fixedValues]);

  const updateFixedValues = (update: (prev: FixedValues) => FixedValues): FixedValues => {
    const next = update(latestFixedValues.current);
    latestFixedValues.current = next;
    importContext.setFixedValues(next);
    return next;
  };

  const isTimeSideEmpty = (side: Side, values: FixedValues['values']) => (
    !TIME_GROUPS[side].some((key) => isFixedValueSet(values[key]))
  );

  // 時間指定の 6 項目がすべて空なら、API はその側を固定していないと扱い地点マスタの時刻を補う。
  // 画面だけ固定モードに残すと食い違うので、その側を列モードに戻す（D8）
  const revertTimeSideToColumn = (side: Side) => {
    const keys = TIME_GROUPS[side];
    setSwitchedToFixedKeys((prev) => new Set([...prev].filter((it) => !keys.includes(it))));
    updateFixedValues((prev) => ({ ...prev, values: withoutKeys(prev.values, keys) }));
  };

  const setFixedValue = (key: string, value: FixedValue) => {
    const next = updateFixedValues((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));
    const side = timeGroupOf(key);
    if (side && isTimeSideEmpty(side, next.values)) revertTimeSideToColumn(side);
  };

  const [switchedToFixedKeys, setSwitchedToFixedKeys] = useState<Set<string>>(
    () => fixedModeKeysOf(importContext.fixedValues)
  );
  const savedFixedKeys = useMemo(() => fixedModeKeysOf(importContext.fixedValues), [importContext.fixedValues]);
  const isFixedMode = (key: string) => switchedToFixedKeys.has(key) || savedFixedKeys.has(key);

  const switchToFixed = (keys: string[]) => {
    setSwitchedToFixedKeys((prev) => new Set([...prev, ...keys]));
    clearMappings(keys);
  };

  const switchMode = (key: string, mode: Mode) => {
    const keys = keysSwitchedWith(key);
    if (mode === 'fixed') {
      switchToFixed(keys);
      return;
    }

    setSwitchedToFixedKeys((prev) => new Set([...prev].filter((it) => !keys.includes(it))));
    const side = key === 'loading_name' ? 'loading' : key === 'unloading_name' ? 'unloading' : null;
    updateFixedValues((prev) => ({
      ...prev,
      values: withoutKeys(prev.values, keys),
      ...(side ? { [`${side}_place_id`]: null } : {}),
    }));
  };

  // マスタに値のある項目だけを固定値にして列の対応を外す。値の無い項目は、固定値なら
  // （マスタの反映で値が消えて）空の固定値のまま残し、列なら列の対応を残す（決定 3）。
  // 時間指定はマスタに時刻が 1 つでもあれば、その側の 6 項目をまとめて固定値にする（D8）。
  const switchMasterKeysToFixed = (keys: string[], next: FixedValues) => {
    const keysWithValue = keys.filter((key) => isFixedValueSet(next.values[key])).flatMap(keysSwitchedWith);
    // 時刻の無い地点に選び直した側は、固定値モードを残さず列モードに戻す
    const emptyTimeSides = (['loading', 'unloading'] as Side[]).filter(
      (side) => TIME_GROUPS[side].some((key) => keys.includes(key)) && isTimeSideEmpty(side, next.values)
    );
    const emptyTimeKeys = emptyTimeSides.flatMap((side) => TIME_GROUPS[side]);
    const keptFixedKeys = keys.filter((key) => isFixedMode(key) && !emptyTimeKeys.includes(key));
    setSwitchedToFixedKeys((prev) => new Set(
      [...prev, ...keptFixedKeys, ...keysWithValue].filter((key) => !emptyTimeKeys.includes(key))
    ));
    clearMappings(keysWithValue);
  };

  const selectShipper = (shipper: ShipperEntity) => {
    const next = updateFixedValues((prev) => applyShipperMaster(prev, shipper));
    switchMasterKeysToFixed(SHIPPER_KEYS, next);
  };

  const selectPlace = (side: Side, place: PlaceEntity | null) => {
    if (!place) {
      updateFixedValues((prev) => ({ ...prev, [`${side}_place_id`]: null }));
      return;
    }

    const next = updateFixedValues((prev) => applyPlaceMaster(prev, side, place));
    switchMasterKeysToFixed(placeKeysOf(side), next);
  };

  const [addressRowCounts, setAddressRowCounts] = useState<Record<string, number>>({
    loading_address: Math.max(addressMappingSelections(importContext.mappings.loading_address).length, 1),
    unloading_address: Math.max(addressMappingSelections(importContext.mappings.unloading_address).length, 1),
  });

  useEffect(() => {
    setAddressRowCounts({
      loading_address: Math.max(addressMappingSelections(importContext.mappings.loading_address).length, 1),
      unloading_address: Math.max(addressMappingSelections(importContext.mappings.unloading_address).length, 1),
    });
    // 行数は選択値のクリアでは縮めず、変換ルール切替時だけ再初期化する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importContext.selectedRuleId]);

  const [hasDatetimeOnlyColumnsError, setHasDatetimeOnlyColumnsError] = useState<string[]>([]);
  useEffect(() => {
    if (!importContext.only_datetime_csv_columns) return;
    if (!importContext.mappings) return;

    const errorColumns = invalidSingleValueMappings(
      ['loading_on', 'unloading_on'],
      importContext.mappings,
      importContext.only_datetime_csv_columns
    );

    setHasDatetimeOnlyColumnsError(errorColumns);
  }, [importContext.mappings, importContext.only_datetime_csv_columns]);

  const existRequiredColumns = requiredColumns
    .map((obj) => obj.en)
    .every((en) => (
      mappingValues(importContext.mappings[en]).length > 0 || isFixedValueSet(importContext.fixedValues.values[en])
    ));

  const renderColumnInput = (obj: { en: string; ja: string; required: boolean; onlyData?: boolean }) => (
    isAddressMapping(obj.en) ? (
      <Stack spacing={1}>
        {buildAddressMappingRows(importContext.mappings[obj.en], addressRowCounts[obj.en]).map((selectedColumn, index, rows) => (
          <Stack key={[obj.en, index].join('-')} direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 24,
                height: 24,
                minWidth: 24,
                borderRadius: '50%',
                backgroundColor: '#EAF4FF',
                color: '#4094F7',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {index + 1}
            </Box>
            <Autocomplete
              disablePortal
              fullWidth
              value={selectedColumn}
              options={selectableColumns(obj, selectedColumn)}
              onChange={(_, value) => {
                const nextSelections = setAddressMappingSelection(importContext.mappings[obj.en], index, value);
                updateMappings(obj.en, nextSelections.length > 0 ? nextSelections : null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`住所${index + 1}`}
                  size="small"
                />
              )}
            />
            <IconButton
              size="small"
              onClick={() => {
                if (!selectedColumn) {
                  setAddressRowCounts((prev) => ({
                    ...prev,
                    [obj.en]: Math.max(prev[obj.en] - 1, 1),
                  }));
                  return;
                }

                const nextSelections = removeAddressMappingSelection(importContext.mappings[obj.en], index);
                updateMappings(obj.en, nextSelections.length > 0 ? nextSelections : null);
                setAddressRowCounts((prev) => ({
                  ...prev,
                  [obj.en]: nextAddressMappingRowCountAfterRemoval(prev[obj.en], nextSelections.length),
                }));
              }}
              disabled={!selectedColumn && rows.length === 1}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Typography variant="caption" color="text.secondary">
          最大3列まで選択できます。選択順に結合します。
        </Typography>
        {canAddAddressMappingRow(addressRowCounts[obj.en]) && (
          <Button
            variant="text"
            startIcon={<AddIcon />}
            sx={{ width: 'fit-content' }}
            onClick={() => {
              setAddressRowCounts((prev) => ({
                ...prev,
                [obj.en]: Math.min(prev[obj.en] + 1, MAX_ADDRESS_MAPPING_ROWS),
              }));
            }}
          >
            列を追加
          </Button>
        )}
      </Stack>
    ) : (
      <Autocomplete
        disablePortal
        onChange={(_, value) => {
          if (value) {
            updateMappings(obj.en, value);
          } else {
            updateMappings(obj.en, null);
          }
        }}
        options={selectableColumns(obj, singleMappingValue(importContext.mappings[obj.en]))}
        value={singleMappingValue(importContext.mappings[obj.en])}
        renderInput={(params) => (
          <TextField
            {...params}
            required={obj.required}
            label={`${obj.ja}に対応する列名を選択`}
            error={
              [
                hasDatetimeOnlyColumnsError.includes(obj.en)
              ].some((bool) => bool)
                ? true
                : obj.required ? mappingValues(importContext.mappings[obj.en]).length === 0 : false
            }
            helperText={
              [
                hasDatetimeOnlyColumnsError.includes(obj.en)
              ].some((bool) => bool)
                ? '取り込みができない値が存在します。エクセルファイルを確認してください'
                : undefined
            }
            size="small"
          />
        )}
      />
    )
  );

  const renderPreview = (key: string, int: number, fixed: boolean) => {
    const factor = factorOfField(key, importContext.weightFactor, importContext.volumeFactor);

    if (fixed) {
      const text = fixedValuePreview(key, importContext.fixedValues.values[key], previewLabels);
      return factor && text !== '-' ? <FactorPreview value={text} factor={factor.factor} fixed /> : text;
    }

    if (!importContext?.sampleValues) return '-';
    if (!importContext?.sampleValues[int]) return '-';
    if (!importContext.mappings[key]) return '-';

    const text = mappingValues(importContext.mappings[key])
      .map((csvColumn) => importContext?.sampleValues[int][csvColumn]?.original)
      .filter((value) => !!value)
      .join('');
    if (!text) return '-';

    return factor ? <FactorPreview value={text} factor={factor.factor} fixed={false} /> : text;
  };

  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight}px)`, []);

  return (
    <Box
      sx={{
        height: contentHeight,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box>
        <Header step={2} />
      </Box>
      <Box
        flexGrow={1}
        paddingX={2}
        paddingY={2}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflow: 'auto', // スクロール可能にする
        }}
      >
        <Stack spacing={2} sx={{ width: '100%' }}>
          <FactorNotice weightFactor={importContext.weightFactor} volumeFactor={importContext.volumeFactor} />
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: 350
                    }}
                  >
                    対応する列名
                  </TableCell>
                  <TableCell>プレビュー1</TableCell>
                  <TableCell>プレビュー2</TableCell>
                  <TableCell>プレビュー3</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attributesForImport.map((obj) => {
                  const fixedType = fixedTypeOf(obj.en);
                  const fixed = !!fixedType && isFixedMode(obj.en);
                  const factor = factorOfField(obj.en, importContext.weightFactor, importContext.volumeFactor);
                  const timeSide = timeGroupOf(obj.en);
                  const placeSide: Side | null = fixedType === 'place' ? (obj.en === 'loading_name' ? 'loading' : 'unloading') : null;

                  return (
                    <TableRow
                      key={['row', obj.en].join('-')}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell
                        component="th" scope="row"
                        sx={{
                          width: 350
                        }}
                      >
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                              {obj.ja}
                              {obj.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                            </Typography>
                            {factor && <FactorChip {...factor} fixed={fixed} />}
                            <Select
                              size="small"
                              value={fixed ? 'fixed' : 'column'}
                              onChange={(event) => switchMode(obj.en, event.target.value)}
                              sx={{ fontSize: 12, minWidth: 148 }}
                              SelectDisplayProps={{ 'aria-label': `${obj.ja}の取得方法`, 'aria-labelledby': undefined }}
                            >
                              <MenuItem value="column">元データ列から取得</MenuItem>
                              <MenuItem value="fixed" disabled={!fixedType}>
                                {fixedType ? '固定値を設定' : '固定値を設定（不可）'}
                              </MenuItem>
                            </Select>
                          </Stack>
                          {timeSide && (
                            <Typography variant="caption" color="text.secondary">
                              {`時間指定は${SIDE_LABELS[timeSide]}の 6 項目をまとめて切り替えます`}
                            </Typography>
                          )}
                          {fixed && fixedType ? (
                            <FixedValueInput
                              fieldKey={obj.en}
                              label={obj.ja}
                              type={fixedType}
                              value={importContext.fixedValues.values[obj.en]}
                              onChange={(value) => setFixedValue(obj.en, value)}
                              onShipperSelect={selectShipper}
                              onPlaceSelect={placeSide ? (place) => selectPlace(placeSide, place) : undefined}
                              companyId={companyId}
                              shippers={shipperOptions}
                              places={placeSide ? placeOptions[placeSide] : undefined}
                              loading={fixedType === 'shipper' ? isLoadingShippers : isLoadingPlaces}
                            />
                          ) : renderColumnInput(obj)}
                          {factor && <FactorHint {...factor} fixed={fixed} />}
                        </Stack>
                      </TableCell>
                      {[0, 1, 2].map((int) => (
                        <TableCell
                          key={['samples', int].join('-')}
                        >
                          {renderPreview(obj.en, int, fixed)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Box>
      <Box>
        <Footer
          handleBackButton={() => {
            importContext.setFile(null);
            importContext.setStep(1);
          }}
          handleNextButton={() => {
            importContext.setIsLoading(true);

            importContext.patchImportCacheMappings()
              .then(() => {
                importContext.setStep(3);
              })
              .catch((e) => {
                const message = badRequestMessage(e, (key) => attributesForImport.find((it) => it.en === key)?.ja ?? key);
                if (message) {
                  enqueueSnackbar(message, { variant: 'error' });
                  return;
                }
                enqueueSnackbar(fiveZeroZeroErrorMessage);
                throw e;
              })
              .finally(() => {
                importContext.setIsLoading(false);
              });
          }}
          disabled={
            [
              hasDatetimeOnlyColumnsError.length
            ].some((num) => !!num) || !existRequiredColumns
          }
        />
      </Box>
    </Box>
  );
});

export default Mapping;
